// CommentsContext.js
import { createContext, useContext, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const CommentsContext = createContext();

export const CommentsProvider = ({ children }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  const getUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  };

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ✅ Fetch Comments
  const fetchComments = useCallback(async (taskId) => {
    if (!taskId) return;

    const token = getToken();
    if (!token) return;

    setComments([]);
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BASE_URL}/comments/${taskId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch comments");
        return;
      }

      const result = await parseJson(res);

      if (!result) {
        setError("Invalid server response");
        return;
      }

      if (result.status === 200) {
        const transformedComments = (result.data || []).map((comment) => ({
          id: comment.id,
          user:
            `${comment.first_name || ""} ${comment.last_name || ""}`.trim() ||
            "Unknown",
          message: comment.comment,
          timestamp: comment.create_audit_time || null,
          type: "user",
        }));

        setComments(transformedComments);
      } else {
        setError(result.message || "Failed to fetch comments");
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
      setError("Error fetching comments");
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Add Comment
  const addComment = useCallback(
    async (taskId, commentText) => {
      if (!taskId) {
        return {
          success: false,
          message: "Task ID is required",
        };
      }

      const token = getToken();
      const user = getUser();
      const userId = user?.id || null;

      setLoading(true);

      const payload = {
        user_id: userId,
        comment: commentText,
        create_audit_id: userId,
      };

      try {
        const res = await fetch(`${BASE_URL}/comment/${taskId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const result = await parseJson(res);

          return {
            success: false,
            message: result?.message || "Failed to add comment",
          };
        }

        const result = await parseJson(res);

        if (!result) {
          return {
            success: false,
            message: "Invalid server response",
          };
        }

        if (result.status === 201) {
          await fetchComments(taskId);

          toast.success(result.message || "Comment added successfully");

          return {
            success: true,
            message: result.message,
            data: result,
          };
        }

        return {
          success: false,
          message: result.message || "Failed to add comment",
        };
      } catch (err) {
        console.error("Error adding comment:", err);

        return {
          success: false,
          message: "Error adding comment",
        };
      } finally {
        setLoading(false);
      }
    },
    [fetchComments]
  );

  // ✅ Clear comments
  const clearComments = useCallback(() => {
    setComments([]);
  }, []);

  return (
    <CommentsContext.Provider
      value={{
        comments,
        loading,
        error,
        fetchComments,
        addComment,
        clearComments,
      }}
    >
      {children}
    </CommentsContext.Provider>
  );
};

export const useComments = () => useContext(CommentsContext);